/* ──────────────────────────────────────────────────────────────
 * ExportStatusPanel
 *
 * Displays a right-aligned "Download Files" button.
 * Clicking the button opens a centered popup containing:
 *   - Overall export progress
 *   - Download all ZIP action
 *   - Individual export files
 *   - Independent file download actions
 *
 * Failed files:
 *   - No failure status or failure details are displayed
 *   - The download action remains disabled
 *
 * Ready files:
 *   - The status chip is hidden once a file is ready
 *
 * Job storage:
 *   - Jobs are identified using the composite jobId + sampleId key
 * ────────────────────────────────────────────────────────────── */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import LinearProgress from '@mui/material/LinearProgress';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';

import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import CloseIcon from '@mui/icons-material/Close';
import FolderZipOutlinedIcon from '@mui/icons-material/FolderZipOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';

import { getJobStatus } from '@/api/jobsApi';
import { jobKey, useJobStore } from '@/stores/useJobStore';
import { useUIStore } from '@/stores/useUIStore';
import type { FileStatus } from '@/types/job';

/** Polling interval in milliseconds. */
const POLL_INTERVAL = 2000;

/** Display labels for each export file type. */
const FILE_TYPE_LABELS: Record<string, string> = {
  marc: 'MARC',
  synera: 'Synera',
  pdf: 'PDF',
  ppt: 'PPT ',
  exp: 'EXP',
  stp: 'STEP',
  download_all: 'Download All',
};

/**
 * File icons use the same assets as ExportActions.
 */
const FILE_TYPE_ICONS: Record<
  string,
  {
    src: string;
    alt: string;
    width: number;
    height: number;
    filter?: string;
  }
> = {
  synera: {
    src: '/assets/synera_logo.png',
    alt: 'Synera',
    width: 68,
    height: 35,
  },
  marc: {
    src: '/assets/marc_logo.png',
    alt: 'MARC',
    width: 22,
    height: 22,
  },
  pdf: {
    src: '/assets/pdf_logo.png',
    alt: 'PDF',
    width: 22,
    height: 22,
  },
  ppt: {
    src: '/assets/ppt_logo.png',
    alt: 'PPT',
    width: 22,
    height: 22,
  },
  stp: {
    src: '/assets/step.png',
    alt: 'STEP',
    width: 22,
    height: 22,
  },
  exp: {
    src: '/assets/NX_logo.svg',
    alt: 'EXP',
    width: 22,
    height: 22,
  },
};

function getStatusColor(
  status: FileStatus,
): 'default' | 'info' | 'success' | 'error' | 'warning' {
  switch (status) {
    case 'pending':
      return 'default';

    case 'generating':
    case 'uploading':
      return 'info';

    case 'ready':
      return 'success';

    case 'failed':
      return 'error';

    default:
      return 'default';
  }
}

function getStatusIcon(status: FileStatus) {
  switch (status) {
    case 'ready':
      return <CheckCircleIcon fontSize="small" />;

    case 'generating':
    case 'uploading':
      return <HourglassTopIcon fontSize="small" />;

    default:
      return undefined;
  }
}

function getStatusLabel(status: FileStatus): string {
  switch (status) {
    case 'pending':
      return 'Pending';

    case 'generating':
      return 'Generating';

    case 'uploading':
      return 'Uploading';

    case 'ready':
      return 'Ready';

    case 'failed':
      return 'Unavailable';

    default:
      return status;
  }
}

function getDownloadTooltip(
  fileLabel: string,
  canDownload: boolean,
): string {
  if (canDownload) {
    return `Download ${fileLabel}`;
  }

  return `${fileLabel} download is unavailable`;
}

interface ExportFileIconProps {
  fileType: string;
}

const ExportFileIcon: React.FC<ExportFileIconProps> = ({
  fileType,
}) => {
  const icon = FILE_TYPE_ICONS[fileType];

  if (!icon) {
    return (
      <Box
        sx={{
          width: 32,
          height: 32,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          borderRadius: 1,
          bgcolor: 'grey.100',
          color: 'text.secondary',
        }}
      >
        <InsertDriveFileOutlinedIcon sx={{ fontSize: 20 }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: 32,
        height: 32,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        borderRadius: 1,
        bgcolor: 'grey.50',
        border: '1px solid',
        borderColor:'grey.200',
        px:  0,
        boxSizing: 'border-box',
      }}
    >
      <Box
        component="img"
        src={icon.src}
        alt={icon.alt}
        sx={{
          width: icon.width,
          height: icon.height,
          objectFit: 'contain',
          filter: icon.filter ?? 'none',
        }}
      />
    </Box>
  );
};

const ExportStatusPanel: React.FC = () => {
  const [isPanelOpen, setIsPanelOpen] = useState(false);

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(
    null,
  );

  const { currentJobId, currentSampleId, jobs } = useJobStore();

  const updateJobFromResponse = useJobStore(
    (state) => state.updateJobFromResponse,
  );

  const setPolling = useJobStore(
    (state) => state.setPolling,
  );

  /*
   * Look up the current job using jobId + sampleId.
   */
  const currentJob =
    currentJobId && currentSampleId
      ? jobs[jobKey(currentJobId, currentSampleId)] ?? null
      : null;

  const filesEntries = useMemo(
    () => (currentJob ? Object.entries(currentJob.files) : []),
    [currentJob],
  );

  const hasExportJob = filesEntries.length > 0;

  /*
   * Download all is displayed separately as the primary action.
   */
  const zipEntry = useMemo(
    () =>
      filesEntries.find(
        ([fileType]) => fileType === 'download_all',
      ),
    [filesEntries],
  );

  const regularFileEntries = useMemo(
    () =>
      filesEntries.filter(
        ([fileType]) => fileType !== 'download_all',
      ),
    [filesEntries],
  );

  const zipFileInfo = zipEntry?.[1];

  const canDownloadAll =
    zipFileInfo?.status === 'ready' &&
    Boolean(zipFileInfo.download_url);

  /*
   * Failed files are excluded from the displayed overall count.
   * They are still retained in the file list with a disabled
   * download action.
   */
  const visibleProgressEntries = useMemo(
    () =>
      filesEntries.filter(
        ([, fileInfo]) => fileInfo.status !== 'failed',
      ),
    [filesEntries],
  );

  const readyCount = visibleProgressEntries.filter(
    ([, fileInfo]) => fileInfo.status === 'ready',
  ).length;

  const totalCount = visibleProgressEntries.length;

  const progressPercentage =
    totalCount > 0 ? (readyCount / totalCount) * 100 : 0;

  const isJobRunning = currentJob?.status === 'running';

  // ── Polling logic ──────────────────────────────────────────

  useEffect(() => {
    if (!currentJobId || !currentSampleId || !currentJob) {
      return;
    }

    if (currentJob.status !== 'running') {
      return;
    }

    setPolling(true);

    const poll = async () => {
      try {
        const status = await getJobStatus(
          currentJobId,
          currentSampleId,
        );

        updateJobFromResponse(status);

        if (status.status !== 'running') {
          if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
          }

          setPolling(false);
        }
      } catch (error) {
        /*
         * Polling continues after a transient API error.
         */
        console.error('Export status polling failed:', error);
      }
    };

    void poll();

    pollRef.current = setInterval(() => {
      void poll();
    }, POLL_INTERVAL);

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }

      setPolling(false);
    };
  }, [
    currentJobId,
    currentSampleId,
    currentJob?.status,
    updateJobFromResponse,
    setPolling,
  ]);

  /*
   * Close the dialog when the job is removed or reset.
   */
  useEffect(() => {
    if (!hasExportJob) {
      setIsPanelOpen(false);
    }
  }, [hasExportJob]);

  // ── Download handler ───────────────────────────────────────

  const openDialog = useUIStore((s) => s.openDialog);

  const handleDownload = useCallback(
    (downloadUrl: string, fileType?: string) => {
      window.open(
        downloadUrl,
        '_blank',
        'noopener,noreferrer',
      );

      if (fileType === 'marc') {
        openDialog('marcInfoDialog');
      } else if (fileType === 'synera') {
        openDialog('syneraInfoDialog');
      }
    },
    [openDialog],
  );

  // ── Dialog handlers ────────────────────────────────────────

  const handlePanelOpen = useCallback(() => {
    if (!hasExportJob) {
      return;
    }

    setIsPanelOpen(true);
  }, [hasExportJob]);

  const handlePanelClose = useCallback(() => {
    setIsPanelOpen(false);
  }, []);

  return (
    <>
      {/* Download Files trigger */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          width: '100%',
          mt: 1.5,
        }}
      >
        <Tooltip
          title={
            hasExportJob
              ? 'View and download generated export files'
              : 'Export files will be available after export starts'
          }
        >
          <span>
            <Button
              variant="contained"
              disabled={!hasExportJob}
              startIcon={<DownloadIcon />}
              onClick={handlePanelOpen}
              aria-haspopup="dialog"
              aria-expanded={isPanelOpen}
              aria-controls={
                isPanelOpen
                  ? 'export-status-dialog'
                  : undefined
              }
              sx={{
                minHeight: 38,
                backgroundColor: '#2e7d32',
                color: '#ffffff',
                fontWeight: 600,
                fontSize: '0.8125rem',
                textTransform: 'none',
                borderRadius: 1,
                px: 1.75,
                py: 0.75,
                boxShadow: '0 2px 6px rgba(0, 0, 0, 0.22)',

                '&:hover': {
                  backgroundColor: '#1b5e20',
                  boxShadow: '0 3px 8px rgba(0, 0, 0, 0.28)',
                },

                '&:focus-visible': {
                  outline: '2px solid #1b5e20',
                  outlineOffset: '2px',
                },

                '&.Mui-disabled': {
                  backgroundColor: '#a5a8a5',
                  color: '#ffffff',
                  boxShadow: 'none',
                },
              }}
            >
              Download Files
            </Button>
          </span>
        </Tooltip>
      </Box>

      {/* Centered export files dialog */}
      <Dialog
        id="export-status-dialog"
        open={isPanelOpen && hasExportJob}
        onClose={handlePanelClose}
        aria-labelledby="export-status-dialog-title"
        maxWidth="sm"
        fullWidth
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: 'rgba(15, 23, 42, 0.45)',
              backdropFilter: 'blur(2px)',
            },
          },
        }}
        PaperProps={{
          elevation: 18,
          sx: {
            width: {
              xs: 'calc(100% - 24px)',
              sm: 520,
            },
            maxWidth: 520,
            maxHeight: {
              xs: 'calc(100vh - 32px)',
              sm: 560,
            },
            m: {
              xs: 1.5,
              sm: 3,
            },
            borderRadius: 2,
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'grey.200',
            backgroundImage: 'none',
          },
        }}
      >
        {/* Dialog header */}
        <DialogTitle
          id="export-status-dialog-title"
          sx={{
            px: 2,
            py: 1.5,
            bgcolor: '#ffffff',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="subtitle1"
                component="div"
                sx={{
                  color: 'text.primary',
                  fontWeight: 700,
                  lineHeight: 1.3,
                }}
              >
                Export Files
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  mt: 0.25,
                }}
              >
                {isJobRunning && (
                  <CircularProgress
                    size={12}
                    thickness={5}
                    sx={{ color: 'info.main' }}
                  />
                )}

                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    lineHeight: 1.3,
                  }}
                >
                  {isJobRunning
                    ? `${readyCount} of ${totalCount} files ready`
                    : `${readyCount} of ${totalCount} files available`}
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                flexShrink: 0,
              }}
            >
              <Tooltip
                title={
                  canDownloadAll
                    ? 'Download all export files as ZIP'
                    : 'ZIP download will be available when processing is complete'
                }
              >
                <span>
                  <Button
                    variant="contained"
                    size="small"
                    disabled={!canDownloadAll}
                    startIcon={
                      <FolderZipOutlinedIcon
                        sx={{ fontSize: '18px !important' }}
                      />
                    }
                    onClick={() => {
                      if (
                        !canDownloadAll ||
                        !zipFileInfo?.download_url
                      ) {
                        return;
                      }

                      handleDownload(zipFileInfo.download_url);
                    }}
                    aria-label={
                      canDownloadAll
                        ? 'Download all export files as ZIP'
                        : 'Download all export files unavailable'
                    }
                    sx={{
                      minWidth: 'auto',
                      minHeight: 34,
                      px: 1.25,
                      py: 0.5,
                      borderRadius: 1,
                      bgcolor: 'success.main',
                      color: '#ffffff',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      lineHeight: 1.4,
                      textTransform: 'none',
                      whiteSpace: 'nowrap',
                      boxShadow: 'none',

                      '&:hover': {
                        bgcolor: 'success.dark',
                        boxShadow: 'none',
                      },

                      '&.Mui-disabled': {
                        bgcolor: 'grey.200',
                        color: 'grey.500',
                      },
                    }}
                  >
                    Download all
                  </Button>
                </span>
              </Tooltip>

              <Tooltip title="Close">
                <IconButton
                  size="small"
                  onClick={handlePanelClose}
                  aria-label="Close export files popup"
                  sx={{
                    width: 34,
                    height: 34,
                    color: 'text.secondary',
                    borderRadius: 1,

                    '&:hover': {
                      color: 'text.primary',
                      bgcolor: 'grey.100',
                    },
                  }}
                >
                  <CloseIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </DialogTitle>

        {/* Progress displayed directly below header */}
        {isJobRunning && (
          <LinearProgress
            variant="determinate"
            value={progressPercentage}
            aria-label="Export generation progress"
            sx={{
              height: 4,
              bgcolor: 'grey.200',

              '& .MuiLinearProgress-bar': {
                bgcolor: 'success.main',
              },
            }}
          />
        )}

        <Divider />

        <DialogContent
          sx={{
            p: 0,
            bgcolor: 'grey.50',
            overflowY: 'auto',
          }}
        >
          {/* File list */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0.75,
              p: 1.25,
            }}
          >
            {regularFileEntries.map(
              ([fileType, fileInfo]) => {
                const fileLabel =
                  FILE_TYPE_LABELS[fileType] ?? fileType;

                const canDownload =
                  fileInfo.status === 'ready' &&
                  Boolean(fileInfo.download_url);

                const downloadTooltip =
                  getDownloadTooltip(
                    fileLabel,
                    canDownload,
                  );

                /*
                 * Failed information is not displayed.
                 */
                const showStatus =
                  fileInfo.status !== 'failed';

                /*
                 * The status chip is hidden once a file is
                 * ready, as well as when it has failed. The
                 * download icon becomes the visual cue that
                 * the file is ready once the chip disappears.
                 */
                const showStatusChip =
                  fileInfo.status !== 'failed' &&
                  fileInfo.status !== 'ready';

                return (
                  <Box
                    key={fileType}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      minHeight: 52,
                      px: 1.25,
                      py: 0.75,
                      bgcolor: '#ffffff',
                      border: '1px solid',
                      borderColor: 'grey.200',
                      borderRadius: 1.25,
                      transition:
                        'border-color 120ms ease, box-shadow 120ms ease',

                      '&:hover': {
                        borderColor: 'grey.300',
                        boxShadow:
                          '0 2px 5px rgba(15, 23, 42, 0.07)',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.25,
                        minWidth: 0,
                        flex: 1,
                      }}
                    >
                      <ExportFileIcon
                        fileType={fileType}
                      />

                      <Box
                        sx={{
                          minWidth: 0,
                          flex: 1,
                        }}
                      >
                        <Typography
                          variant="body2"
                          title={fileLabel}
                          sx={{
                            color: 'text.primary',
                            fontSize: '0.8125rem',
                            fontWeight: 600,
                            lineHeight: 1.3,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {fileLabel}
                        </Typography>

                        {!showStatus && (
                          <Typography
                            variant="caption"
                            sx={{
                              display: 'block',
                              mt: 0.2,
                              color: 'text.disabled',
                              fontSize: '0.6875rem',
                              lineHeight: 1.2,
                            }}
                          >
                            Not available
                          </Typography>
                        )}
                      </Box>

                      {showStatusChip && (
                        <Chip
                          label={getStatusLabel(
                            fileInfo.status,
                          )}
                          size="small"
                          color={getStatusColor(
                            fileInfo.status,
                          )}
                          icon={getStatusIcon(
                            fileInfo.status,
                          )}
                          variant="outlined"
                          sx={{
                            flexShrink: 0,
                            height: 23,
                            borderRadius: 1,
                            fontSize: '0.6875rem',
                            fontWeight: 500,

                            '& .MuiChip-label': {
                              px: 0.75,
                            },

                            '& .MuiChip-icon': {
                              ml: 0.5,
                              mr: -0.25,
                              fontSize: 14,
                            },
                          }}
                        />
                      )}
                    </Box>

                    <Tooltip title={downloadTooltip}>
                      <span>
                        <IconButton
                          size="small"
                          disabled={!canDownload}
                          aria-label={
                            canDownload
                              ? `Download ${fileLabel}`
                              : `${fileLabel} download unavailable`
                          }
                          onClick={() => {
                            if (
                              !canDownload ||
                              !fileInfo.download_url
                            ) {
                              return;
                            }

                            handleDownload(
                              fileInfo.download_url,
                              fileType,
                            );
                          }}
                          sx={{
                            ml: 1.25,
                            width: 34,
                            height: 34,
                            flexShrink: 0,
                            color: 'success.dark',
                            bgcolor:
                              'rgba(46, 125, 50, 0.08)',
                            borderRadius: 1,

                            '&:hover': {
                              color: '#ffffff',
                              bgcolor: 'success.main',
                            },

                            '&:focus-visible': {
                              outline: '2px solid',
                              outlineColor: 'success.main',
                              outlineOffset: '2px',
                            },

                            '&.Mui-disabled': {
                              color: 'grey.400',
                              bgcolor: 'grey.100',
                            },
                          }}
                        >
                          <DownloadIcon
                            sx={{ fontSize: 19 }}
                          />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                );
              },
            )}

            {regularFileEntries.length === 0 && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: 100,
                  px: 2,
                  py: 3,
                  bgcolor: '#ffffff',
                  border: '1px dashed',
                  borderColor: 'grey.300',
                  borderRadius: 1.25,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ color: 'text.secondary' }}
                >
                  No individual export files are available.
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default React.memo(ExportStatusPanel);